export const EDDIES_GA_MEASUREMENT_ID = "G-6ZQTW2DCXD";

const BOOK_APPOINTMENT_EVENT_ID_RE = /^[A-Za-z0-9_-]{43}$/;
const BOOK_APPOINTMENT_DEDUPE_KEY_PREFIX =
  "eddies-google-book-appointment-v1:";
const dispatchedBookAppointments = new Set<string>();

export type BookingConversion = {
  /** Opaque server-issued ID. Never use an appointment/customer ID here. */
  eventId: string;
  value: number | null;
  currency: "USD";
};

type GoogleTagFunction = NonNullable<Window["gtag"]>;

export const pushToDataLayer = (
  event: string,
  data: Record<string, any> = {}
) => {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    ...data,
  });
};

const ensureGoogleTagFunction = (): GoogleTagFunction | null => {
  if (typeof window === "undefined") return null;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // Google consumes the function's array-like Arguments object from the
      // same dataLayer that the existing GTM container bootstraps.
      window.dataLayer?.push(arguments);
    };
  }

  return window.gtag;
};

const isValidBookingConversion = (
  conversion: BookingConversion,
): boolean =>
  BOOK_APPOINTMENT_EVENT_ID_RE.test(conversion.eventId) &&
  conversion.currency === "USD" &&
  (conversion.value === null ||
    (typeof conversion.value === "number" &&
      Number.isFinite(conversion.value) &&
      conversion.value >= 0));

const isDevHarnessPath = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    window.location.pathname === "/dev" ||
    window.location.pathname.startsWith("/dev/")
  );
};

const wasBookAppointmentDispatched = (storageKey: string): boolean => {
  if (dispatchedBookAppointments.has(storageKey)) return true;

  try {
    return window.sessionStorage.getItem(storageKey) === "1";
  } catch {
    // Storage can be unavailable in hardened/private browser contexts. The
    // in-memory guard still protects the current document in that case.
    return false;
  }
};

const markBookAppointmentDispatched = (storageKey: string): void => {
  dispatchedBookAppointments.add(storageKey);
  try {
    window.sessionStorage.setItem(storageKey, "1");
  } catch {
    // The event is already queued. Storage failure must not affect booking.
  }
};

/**
 * Sends Eddie's canonical completed-booking event through the Google tag
 * configured by GTM. The event is emitted from the authoritative create
 * response, never from the public review page and never for a reschedule.
 */
export const trackBookAppointment = (
  conversion: BookingConversion,
): boolean => {
  try {
    if (
      typeof window === "undefined" ||
      isDevHarnessPath() ||
      !isValidBookingConversion(conversion)
    ) {
      return false;
    }

    const storageKey = `${BOOK_APPOINTMENT_DEDUPE_KEY_PREFIX}${conversion.eventId}`;
    if (wasBookAppointmentDispatched(storageKey)) return false;

    const gtag = ensureGoogleTagFunction();
    if (!gtag) return false;

    const parameters: Record<string, unknown> = {
      transaction_id: conversion.eventId,
      send_to: EDDIES_GA_MEASUREMENT_ID,
      openings_surface: "website",
      // Event scope has the highest gtag precedence. Keep this empty even if
      // automatic enhanced conversions are enabled remotely.
      user_data: {},
    };

    if (conversion.value !== null) {
      parameters.value = conversion.value;
      parameters.currency = conversion.currency;
    }

    gtag("event", "book_appointment", parameters);
    markBookAppointmentDispatched(storageKey);
    return true;
  } catch {
    // Analytics must never interrupt a completed booking or its redirect.
    return false;
  }
};
